import { useState, useMemo } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { api, apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { TinyCard } from "@/components/Card3D";
import { toast } from "sonner";
import { ChevronsUp, Coins, Flame, Wand2, Lock, Check, Sparkles } from "lucide-react";

const RARITY_TONE = {
  common: "text-white/70 border-white/20",
  uncommon: "text-emerald-300 border-emerald-400/40",
  rare: "text-cyan-300 border-cyan-400/40",
  epic: "text-fuchsia-300 border-fuchsia-400/40",
  legendary: "text-amber-300 border-amber-400/40",
  goddess: "text-rose-300 border-rose-400/40",
};

export function AscensionForge({ onAscended }) {
  const qc = useQueryClient();
  const { user, refresh } = useAuth();
  const [rarity, setRarity] = useState(null);
  const [selected, setSelected] = useState(() => new Set());

  const { data: info } = useQuery({ queryKey: ["ascend-info"], queryFn: async () => (await api.get("/ascend/info")).data });
  const { data: cards } = useQuery({ queryKey: ["my-cards"], queryFn: async () => (await api.get("/cards/mine")).data });

  const tiers = info?.tiers || [];
  const sacrifice = info?.sacrifice || 10;
  const activeTier = tiers.find((t) => t.rarity === rarity) || null;

  // Only cards of the chosen rarity that aren't committed anywhere else.
  const pool = useMemo(() => {
    if (!rarity) return [];
    return (cards || []).filter((c) => c.rarity === rarity && !c.in_use);
  }, [cards, rarity]);

  const pickRarity = (r) => {
    setRarity(r);
    setSelected(new Set());
  };

  const toggle = (id) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else if (next.size < sacrifice) next.add(id);
      return next;
    });
  };

  const autoPick = () => {
    const ids = pool.slice(0, sacrifice).map((c) => c.id);
    setSelected(new Set(ids));
  };

  const ascend = useMutation({
    mutationFn: async () =>
      (await api.post("/ascend", { rarity, card_ids: [...selected] })).data,
    onSuccess: (d) => {
      toast.success(`Ascended to ${d.to_rarity}!`);
      setSelected(new Set());
      qc.invalidateQueries({ queryKey: ["my-cards"] });
      qc.invalidateQueries({ queryKey: ["ascend-info"] });
      refresh();
      onAscended?.(d.card);
    },
    onError: (e) => toast.error(apiError(e, "Ascension failed")),
  });

  const count = selected.size;
  const cost = activeTier?.cost ?? 0;
  const credits = user?.credits ?? 0;
  const ready = !!activeTier && count === sacrifice && activeTier.next_available && credits >= cost;

  return (
    <div className="mt-10 hud-pill rounded-2xl p-6 relative overflow-hidden" data-testid="ascension-forge">
      <div className="absolute inset-0 holo-scan opacity-[0.07] pointer-events-none" />
      <div className="flex items-center gap-3 mb-1">
        <div className="h-11 w-11 rounded-xl bg-amber-400/15 border border-amber-300/30 flex items-center justify-center">
          <ChevronsUp className="h-6 w-6 text-amber-300" />
        </div>
        <div>
          <h2 className="font-display text-2xl sm:text-3xl text-white tracking-[0.04em]">ASCENSION FORGE</h2>
          <p className="text-white/50 text-sm">
            Trade up — sacrifice <span className="text-white/80">{sacrifice}</span> cards of one rarity to forge <span className="text-amber-300">1 of the next tier</span>. The {sacrifice} are recycled back to the store.
          </p>
        </div>
      </div>

      {/* Rarity tiers */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-2.5 mt-5">
        {tiers.map((t) => {
          const enough = t.owned >= t.required;
          const blocked = !t.next_available;
          const sel = rarity === t.rarity;
          const disabled = !enough || blocked;
          return (
            <button
              key={t.rarity}
              data-testid={`ascend-tier-${t.rarity}`}
              disabled={disabled}
              onClick={() => pickRarity(t.rarity)}
              className={`text-left rounded-xl border p-3 transition-all ${
                sel ? "border-amber-300 bg-amber-300/10" : "border-white/10 bg-white/[0.03] hover:bg-white/[0.06]"
              } ${disabled ? "opacity-45 cursor-not-allowed" : ""}`}
            >
              <div className="flex items-center justify-between">
                <span className={`font-display text-sm uppercase tracking-[0.06em] ${RARITY_TONE[t.rarity]?.split(" ")[0] || "text-white"}`}>{t.rarity}</span>
                {sel && <Check className="h-3.5 w-3.5 text-amber-300" />}
              </div>
              <div className="font-mono text-[11px] text-white/55 mt-1">{t.owned} / {t.required} owned</div>
              <div className="flex items-center gap-1 mt-1.5 text-[10px] font-mono text-white/45">
                <ChevronsUp className="h-3 w-3 text-amber-300" /> {t.next_rarity}
              </div>
              <div className="flex items-center gap-1 mt-0.5 text-[10px] font-mono text-amber-300/90">
                <Coins className="h-3 w-3" /> {t.cost.toLocaleString()}
              </div>
              {blocked && <div className="flex items-center gap-1 mt-1 text-[9px] text-red-300/80"><Lock className="h-2.5 w-2.5" /> No {t.next_rarity} supply</div>}
            </button>
          );
        })}
      </div>

      {/* Card picker */}
      {activeTier && (
        <div className="mt-6">
          <div className="flex flex-wrap items-center gap-2 mb-3">
            <span className="font-mono text-[11px] uppercase tracking-[0.3em] text-white/45 mr-auto">
              Choose {sacrifice} {rarity} to sacrifice
            </span>
            <span data-testid="ascend-counter" className={`font-mono text-xs ${count === sacrifice ? "text-amber-300" : "text-white/50"}`}>{count} / {sacrifice} selected</span>
            <Button data-testid="ascend-autopick" size="sm" variant="outline" disabled={pool.length === 0} onClick={autoPick} className="border-white/15 text-white/70 h-7">
              <Wand2 className="h-3.5 w-3.5 mr-1" /> Auto-pick {sacrifice}
            </Button>
          </div>

          {pool.length < sacrifice ? (
            <div className="hud-pill rounded-xl p-6 text-center text-white/45 text-sm">
              You only have {pool.length} unlocked {rarity} card{pool.length === 1 ? "" : "s"} — you need {sacrifice}.
            </div>
          ) : (
            <div className="grid grid-cols-3 sm:grid-cols-5 lg:grid-cols-7 gap-2.5 max-h-[44vh] overflow-y-auto pr-1">
              {pool.map((c) => {
                const isSel = selected.has(c.id);
                const atCap = count >= sacrifice && !isSel;
                return (
                  <div
                    key={c.id}
                    data-testid={`ascend-pick-${c.id}`}
                    className={`relative rounded-xl transition-transform ${isSel ? "ring-2 ring-amber-300 ring-offset-2 ring-offset-[hsl(220_22%_6%)] scale-[1.03]" : atCap ? "opacity-40" : ""}`}
                  >
                    <TinyCard card={c} idKey={c.id} previewSide="top" onClick={() => toggle(c.id)} />
                    {isSel && (
                      <div className="absolute top-1 right-1 h-5 w-5 rounded-full bg-amber-300 text-black flex items-center justify-center shadow z-10">
                        <Flame className="h-3 w-3" />
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}

          <div className="flex flex-col sm:flex-row sm:items-center gap-3 mt-5">
            <p className="text-[11px] text-white/45 flex-1">
              {activeTier.next_available
                ? <>Forges 1 random <span className={RARITY_TONE[activeTier.next_rarity]?.split(" ")[0]}>{activeTier.next_rarity}</span> card. Costs <span className="text-amber-300">{cost.toLocaleString()}c</span>. Your balance: {credits.toLocaleString()}c.</>
                : <span className="text-red-300">No {activeTier.next_rarity} editions are mintable right now — ascension is blocked.</span>}
            </p>
            <Button
              data-testid="ascend-submit"
              disabled={!ready || ascend.isPending}
              onClick={() => ascend.mutate()}
              className="bg-amber-300 text-black hover:bg-amber-200 disabled:opacity-40 font-medium"
            >
              <Sparkles className="h-4 w-4 mr-1.5" />
              {ascend.isPending ? "Ascending…" : credits < cost ? "Not enough credits" : `Ascend · ${cost.toLocaleString()}c`}
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
